"use client";

import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import type { PdfApiRequestPayload } from "@/src/lib/pdf/api-contract";
import { detectPdfBrowserEngine } from "@/src/lib/pdf/browser-engine";
import {
  PdfDownloadError,
  requestPdfDownload,
} from "@/src/lib/pdf/download";
import { sanitizePdfFilename } from "@/src/lib/pdf/filename";
import {
  defaultPdfOptions,
  type PdfOptions,
} from "@/src/lib/pdf/print-document";
import { getFontStack } from "@/src/lib/pdf/fonts";
import type { PdfFontKey } from "@/src/lib/pdf/fonts";
import {
  detectDocumentDirection,
  detectDocumentLang,
} from "@/src/lib/pdf/document-direction";
import { detectNeededScriptFallbacks } from "@/src/lib/pdf/script-fallbacks";
import { shouldUseSamplePreviewSnapshot } from "@/src/lib/pdf/sample-preview-snapshot";
import {
  buildPersistedPreferences,
  DEFAULT_EDITOR_FONT_FAMILY,
  DEFAULT_EDITOR_FONT_SIZE,
  DEFAULT_SPLIT_RATIO,
  loadPersistedPreferences,
  mergePdfOptionsWithDefaults,
  savePersistedPreferences,
  type EditorPreferencesSource,
} from "@/src/lib/storage/user-preferences";
import type { ChatShareProvider } from "@/src/lib/chat-share/types";
import { EditorFontStyles } from "./editor/editor-font-styles";
import {
  EditorPane,
  type EditorPaneHandle,
} from "./editor/editor-pane";
import {
  editorHistoryLimit,
  initialDocumentFilename,
  preferencesCommitDelayMs,
  previewDebounceMs,
  sampleMarkdown,
} from "./editor/constants";
import { setEmbeddedEditorDocument } from "./editor/embedded-editor-document";
import type { ReadMarkdownFileSuccess } from "./editor/read-markdown-file";
import type {
  EditorCommand,
  EditorSelection,
  EditorState,
  WorkspaceTab,
} from "./editor/types";
import { MobileTabs } from "./layout/mobile-tabs";
import { ResizableSplit } from "./layout/resizable-split";
import { StatusBar } from "./layout/status-bar";
import { useScrollSync } from "./layout/use-scroll-sync";
import { WorkspaceToolbar } from "./layout/workspace-toolbar";
import { PreviewPane } from "./preview/preview-pane";
import type { DownloadState, PreviewState } from "./preview/types";
import { saveBlobAsDownload } from "./utils/download-blob";
import {
  clearDownloadCooldown,
  PDF_DOWNLOAD_COOLDOWN_SECONDS,
  readCooldownRemainingSeconds,
  startDownloadCooldown,
} from "./utils/download-cooldown";

/** Tailwind `md` breakpoint — desktop split-pane preview auto-starts here. */
export const DESKTOP_PREVIEW_MEDIA_QUERY = "(min-width: 768px)";

export type EditorWorkspaceVariant = "embedded" | "fullscreen";

export type EditorWorkspaceDocument = {
  markdown: string;
  filename: string;
};

export type EditorWorkspaceHandle = {
  replaceDocument: (document: EditorWorkspaceDocument) => boolean;
};

export const EditorWorkspace = forwardRef<
  EditorWorkspaceHandle,
  Readonly<{
    initialMarkdown?: string;
    initialFilename?: string;
    /**
     * Embedded shows the full toolbar but keeps settings in-memory only.
     * Fullscreen loads/saves preferences under `editorSource`.
     */
    variant?: EditorWorkspaceVariant;
    /** Preferences namespace for fullscreen persistence and Maximize handoff. */
    editorSource?: EditorPreferencesSource;
    /** When set (Maximize handoff), seeds PDF options and skips older stored values. */
    initialPdfOptions?: PdfOptions;
    initialEditorFontFamily?: PdfFontKey;
    initialEditorFontSize?: number;
    chatShareProvider?: ChatShareProvider;
    chatShareImportUrl?: string;
    repoMarkdownImport?: boolean;
    repoMarkdownImportUrl?: string;
  }>
>(function EditorWorkspace(
  {
    initialMarkdown = sampleMarkdown,
    initialFilename = initialDocumentFilename,
    variant = "fullscreen",
    editorSource = "editor",
    initialPdfOptions,
    initialEditorFontFamily,
    initialEditorFontSize,
    chatShareProvider,
    chatShareImportUrl,
    repoMarkdownImport = false,
    repoMarkdownImportUrl,
  },
  ref,
) {
  const isEmbedded = variant === "embedded";
  const settingsFromHandoff = initialPdfOptions !== undefined;
  const [activeTab, setActiveTab] = useState<WorkspaceTab>("editor");
  // Server-safe: assume not-desktop until client matchMedia runs. Once true
  // (desktop viewport or mobile Preview tab), stays enabled for the session so
  // markdown→HTML can stay warm while the mobile Editor tab is showing.
  const [previewEnabled, setPreviewEnabled] = useState(false);
  const [isDesktopPreview, setIsDesktopPreview] = useState(false);
  const [pdfOptions, setPdfOptions] = useState(
    () => initialPdfOptions ?? defaultPdfOptions,
  );
  const [editorFontFamily, setEditorFontFamily] = useState<PdfFontKey>(
    () => initialEditorFontFamily ?? DEFAULT_EDITOR_FONT_FAMILY,
  );
  const [editorFontSize, setEditorFontSize] = useState(
    () => initialEditorFontSize ?? DEFAULT_EDITOR_FONT_SIZE,
  );
  const [splitRatio, setSplitRatio] = useState(DEFAULT_SPLIT_RATIO);
  const [filename, setFilename] = useState(initialFilename);
  const [editorState, setEditorState] = useState<EditorState>({
    markdown: initialMarkdown,
    past: [],
    future: [],
  });
  const [previewState, setPreviewState] = useState<PreviewState>({
    status: "rendering",
    rendered: null,
    errorMessage: null,
  });
  /** Bumped on full document replace so preview re-runs even when markdown is unchanged. */
  const [previewRenderNonce, setPreviewRenderNonce] = useState(0);
  const [downloadState, setDownloadState] = useState<DownloadState>({
    status: "idle",
    errorMessage: null,
  });
  const [previewPageCount, setPreviewPageCount] = useState(0);
  const [previewUpdating, setPreviewUpdating] = useState(false);
  const downloadInFlightRef = useRef(false);
  const preferencesLoadedRef = useRef(false);
  const seedMarkdownRef = useRef(initialMarkdown);
  const editorScrollRef = useRef<HTMLDivElement>(null);
  const previewScrollRef = useRef<HTMLDivElement>(null);
  const editorPaneRef = useRef<EditorPaneHandle>(null);
  const previewToolbarCloseRef = useRef<() => void>(() => undefined);
  const markdownRenderCacheRef = useRef<{
    key: string;
    rendered: NonNullable<PreviewState["rendered"]>;
  } | null>(null);
  useScrollSync(editorScrollRef, previewScrollRef);
  const markdown = editorState.markdown;
  const scriptFallbacks = detectNeededScriptFallbacks(markdown);
  const documentDir = detectDocumentDirection(markdown);
  const documentLang = detectDocumentLang(markdown);
  const canDownloadPdf = markdown.trim().length > 0;
  const canUndo = editorState.past.length > 0;
  const canRedo = editorState.future.length > 0;

  useEffect(() => {
    if (!isEmbedded) {
      return;
    }
    setEmbeddedEditorDocument({ markdown, filename });
    return () => {
      setEmbeddedEditorDocument(null);
    };
  }, [filename, isEmbedded, markdown]);

  const registerToolbarClose = useCallback((close: () => void) => {
    previewToolbarCloseRef.current = close;
  }, []);

  const enablePreview = useCallback(() => {
    setPreviewEnabled(true);
  }, []);

  const handleTabChange = useCallback(
    (tab: WorkspaceTab) => {
      setActiveTab(tab);
      if (tab === "preview") {
        enablePreview();
      }
    },
    [enablePreview],
  );

  const runEditorCommand = useCallback((command: EditorCommand) => {
    editorPaneRef.current?.runCommand(command);
  }, []);

  const undoFromToolbar = useCallback(() => {
    editorPaneRef.current?.undo();
  }, []);

  const redoFromToolbar = useCallback(() => {
    editorPaneRef.current?.redo();
  }, []);

  function beginDownloadCooldown(durationSeconds: number) {
    const remainingSeconds = startDownloadCooldown(durationSeconds);
    setDownloadState({
      status: "cooldown",
      remainingSeconds,
      errorMessage: null,
    });
  }

  // Load saved user preferences once the component mounts. Defaults are used for
  // the initial render so server and client output match and hydration stays
  // consistent. Updating state here is intentional: it is the single point where
  // client-side storage rehydrates the UI without affecting SSR output.
  // Embedded keeps settings in-memory only. Maximize handoff settings win over
  // older stored PDF/editor fonts (split ratio still loads from the source key).
  useEffect(() => {
    const remainingSeconds = readCooldownRemainingSeconds();

    preferencesLoadedRef.current = true;

    if (!isEmbedded) {
      const stored = loadPersistedPreferences(editorSource);
      if (settingsFromHandoff) {
        if (stored) {
          setSplitRatio(stored.splitRatio);
        }
      } else if (stored) {
        setPdfOptions(mergePdfOptionsWithDefaults(stored.pdfOptions));
        setEditorFontFamily(stored.editorFontFamily);
        setEditorFontSize(stored.editorFontSize);
        setSplitRatio(stored.splitRatio);
      }
    }

    if (remainingSeconds !== null) {
      setDownloadState({
        status: "cooldown",
        remainingSeconds,
        errorMessage: null,
      });
    }
  }, [editorSource, isEmbedded, settingsFromHandoff]);

  // Desktop (md+) auto-enables preview; mobile waits until the Preview tab.
  // Track the breakpoint separately so new pagination only runs while the pane
  // is visible — a settled preview stays cached across Editor switches until
  // the document revision changes.
  useEffect(() => {
    if (typeof window.matchMedia !== "function") {
      return;
    }

    const mediaQuery = window.matchMedia(DESKTOP_PREVIEW_MEDIA_QUERY);
    const syncDesktopPreview = () => {
      const matches = mediaQuery.matches;
      setIsDesktopPreview(matches);
      if (matches) {
        enablePreview();
      }
    };

    syncDesktopPreview();
    mediaQuery.addEventListener("change", syncDesktopPreview);
    return () => {
      mediaQuery.removeEventListener("change", syncDesktopPreview);
    };
  }, [enablePreview]);

  const previewVisible = isDesktopPreview || activeTab === "preview";

  // Persist PDF options and split ratio whenever they change. Writes are
  // skipped until after the initial load so stored preferences are not
  // overwritten by the first render's defaults. A short debounce prevents
  // repeated localStorage writes while dragging the split pane.
  // Embedded never persists; fullscreen writes under the surface source key.
  useEffect(() => {
    if (isEmbedded || !preferencesLoadedRef.current) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      savePersistedPreferences(
        buildPersistedPreferences(
          pdfOptions,
          splitRatio,
          editorFontFamily,
          editorFontSize,
        ),
        editorSource,
      );
    }, preferencesCommitDelayMs);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [
    editorFontFamily,
    editorFontSize,
    editorSource,
    isEmbedded,
    pdfOptions,
    splitRatio,
  ]);

  // Tick the post-download cooldown so the button label counts down and
  // re-enables when the persisted window expires.
  useEffect(() => {
    if (downloadState.status !== "cooldown") {
      return;
    }

    if (downloadState.remainingSeconds <= 0) {
      clearDownloadCooldown();
      setDownloadState({
        status: "idle",
        errorMessage: null,
      });
      return;
    }

    const timeoutId = window.setTimeout(() => {
      const remainingSeconds = readCooldownRemainingSeconds();

      if (remainingSeconds === null) {
        clearDownloadCooldown();
        setDownloadState({
          status: "idle",
          errorMessage: null,
        });
        return;
      }

      setDownloadState({
        status: "cooldown",
        remainingSeconds,
        errorMessage: null,
      });
    }, 1_000);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [downloadState]);

  function clearDownloadError() {
    setDownloadState((currentState) =>
      currentState.status === "error"
        ? { status: "idle", errorMessage: null }
        : currentState,
    );
  }

  /**
   * While the mobile Editor tab hides the pane, drop stale HTML immediately so
   * switching back to Preview cannot remount the previous document before the
   * debounced render finishes. Warm iframe updates do not rewrite srcdoc, so a
   * mid-render remount would keep showing the old body in the shell.
   */
  function invalidateHiddenPreviewRender(nextMarkdown: string) {
    if (isDesktopPreview || activeTab === "preview" || !previewEnabled) {
      return;
    }

    markdownRenderCacheRef.current = null;
    setPreviewState({
      status: nextMarkdown.trim().length === 0 ? "empty" : "rendering",
      rendered: null,
      errorMessage: null,
    });
  }

  function updateMarkdown(nextMarkdown: string) {
    clearDownloadError();
    if (nextMarkdown === editorState.markdown) {
      return;
    }

    setEditorState((currentState) => ({
      markdown: nextMarkdown,
      past: [
        ...currentState.past.slice(-(editorHistoryLimit - 1)),
        currentState.markdown,
      ],
      future: [],
    }));
    invalidateHiddenPreviewRender(nextMarkdown);
  }

  function updateFilename(nextFilename: string) {
    clearDownloadError();
    setFilename(nextFilename);
  }

  function replaceDocument(
    result: EditorWorkspaceDocument,
    {
      confirmMessage = "Replace the current document? Unsaved edits will be lost.",
    }: Readonly<{ confirmMessage?: string }> = {},
  ) {
    const currentMarkdown = editorState.markdown;
    const hasUnsavedChanges =
      currentMarkdown.trim().length > 0 &&
      currentMarkdown !== seedMarkdownRef.current;

    if (
      hasUnsavedChanges &&
      typeof window !== "undefined" &&
      !window.confirm(confirmMessage)
    ) {
      return false;
    }

    clearDownloadError();
    seedMarkdownRef.current = result.markdown;
    setFilename(result.filename);
    setEditorState({
      markdown: result.markdown,
      past: [],
      future: [],
    });
    markdownRenderCacheRef.current = null;
    // Re-importing the same Markdown would leave status "rendering" forever if we
    // only depended on `markdown` — React bails out and the preview effect never
    // re-runs. Bump a nonce so pagination always refreshes after replace.
    setPreviewRenderNonce((nonce) => nonce + 1);
    setPreviewState({
      status: result.markdown.trim().length === 0 ? "empty" : "rendering",
      rendered: null,
      errorMessage: null,
    });

    return true;
  }

  function replaceDocumentFromFile(result: ReadMarkdownFileSuccess) {
    replaceDocument(result, {
      confirmMessage:
        "Replace the current document with the selected file? Unsaved edits will be lost.",
    });
  }

  useImperativeHandle(ref, () => ({
    replaceDocument: (document) =>
      replaceDocument(document, {
        confirmMessage:
          "Replace the current document with the imported conversation? Unsaved edits will be lost.",
      }),
  }));

  function clearDocument() {
    if (markdown.trim().length === 0 && filename.trim().length === 0) {
      return;
    }

    if (
      typeof window !== "undefined" &&
      !window.confirm("Clear the current document?")
    ) {
      return;
    }

    clearDownloadError();
    seedMarkdownRef.current = "";
    setFilename("");
    setEditorState((currentState) => ({
      markdown: "",
      past:
        currentState.markdown.length > 0
          ? [
              ...currentState.past.slice(-(editorHistoryLimit - 1)),
              currentState.markdown,
            ]
          : currentState.past,
      future: [],
    }));
    markdownRenderCacheRef.current = null;
    invalidateHiddenPreviewRender("");
    setPreviewState({
      status: "empty",
      rendered: null,
      errorMessage: null,
    });
  }

  function updatePdfOptions(nextOptions: PdfOptions) {
    clearDownloadError();
    setPdfOptions(nextOptions);
  }

  function closePreviewToolbarMenu() {
    previewToolbarCloseRef.current();
  }

  async function downloadPdf() {
    if (
      downloadInFlightRef.current ||
      !canDownloadPdf ||
      downloadState.status === "cooldown"
    ) {
      return;
    }

    downloadInFlightRef.current = true;
    setDownloadState({
      status: "generating",
      errorMessage: null,
    });

    const payload = {
      markdown,
      filename: sanitizePdfFilename(filename),
      options: pdfOptions,
      browser: detectPdfBrowserEngine(),
    } satisfies PdfApiRequestPayload;

    try {
      const download = await requestPdfDownload(payload);

      saveBlobAsDownload(download.blob, download.filename);
      beginDownloadCooldown(PDF_DOWNLOAD_COOLDOWN_SECONDS);
    } catch (error: unknown) {
      if (
        error instanceof PdfDownloadError &&
        error.retryAfterSeconds !== null
      ) {
        beginDownloadCooldown(error.retryAfterSeconds);
        return;
      }

      const message =
        error instanceof Error
          ? error.message
          : "PDF download failed.";

      setDownloadState({
        status: "error",
        errorMessage: message,
      });
    } finally {
      downloadInFlightRef.current = false;
    }
  }

  function undoMarkdown(): EditorSelection | null {
    const previousMarkdown = editorState.past.at(-1);

    if (previousMarkdown === undefined) {
      return null;
    }

    setEditorState({
      markdown: previousMarkdown,
      past: editorState.past.slice(0, -1),
      future: [editorState.markdown, ...editorState.future].slice(
        0,
        editorHistoryLimit,
      ),
    });
    invalidateHiddenPreviewRender(previousMarkdown);

    return {
      start: previousMarkdown.length,
      end: previousMarkdown.length,
    };
  }

  function redoMarkdown(): EditorSelection | null {
    const nextMarkdown = editorState.future[0];

    if (nextMarkdown === undefined) {
      return null;
    }

    setEditorState({
      markdown: nextMarkdown,
      past: [
        ...editorState.past.slice(-(editorHistoryLimit - 1)),
        editorState.markdown,
      ],
      future: editorState.future.slice(1),
    });
    invalidateHiddenPreviewRender(nextMarkdown);

    return {
      start: nextMarkdown.length,
      end: nextMarkdown.length,
    };
  }

  useEffect(() => {
    if (!previewEnabled) {
      return;
    }

    let isActive = true;
    const timeoutId = window.setTimeout(async () => {
      if (markdown.trim().length === 0) {
        markdownRenderCacheRef.current = null;
        setPreviewState({
          status: "empty",
          rendered: null,
          errorMessage: null,
        });
        return;
      }

      // Embedded sample: PreviewPane serves the prerendered snapshot. Skip the
      // unified/KaTeX client chunk so PageSpeed does not download it unused.
      if (
        shouldUseSamplePreviewSnapshot({
          preferSampleSnapshot: isEmbedded,
          markdown,
          sampleMarkdown: seedMarkdownRef.current,
          options: pdfOptions,
        })
      ) {
        markdownRenderCacheRef.current = null;
        setPreviewState({
          status: "ready",
          rendered: null,
          errorMessage: null,
        });
        return;
      }

      const scripts = detectNeededScriptFallbacks(markdown);
      const fontStack = getFontStack(pdfOptions.fontFamily, scripts);
      const cacheKey = [
        markdown,
        pdfOptions.fontFamily,
        fontStack,
        pdfOptions.theme,
        pdfOptions.codeTheme,
      ].join("\u0001");
      const cached = markdownRenderCacheRef.current;

      if (cached?.key === cacheKey) {
        setPreviewState({
          status: "ready",
          rendered: cached.rendered,
          errorMessage: null,
        });
        return;
      }

      setPreviewState((previous) => ({
        status: "rendering",
        rendered: previous.rendered,
        errorMessage: null,
      }));

      try {
        const { renderMarkdownToHtml } = await import("@/src/lib/markdown");
        const rendered = await renderMarkdownToHtml(markdown, {
          theme: pdfOptions.theme,
          codeTheme: pdfOptions.codeTheme,
        });

        if (!isActive) {
          return;
        }

        markdownRenderCacheRef.current = { key: cacheKey, rendered };
        setPreviewState({
          status: "ready",
          rendered,
          errorMessage: null,
        });
      } catch (error: unknown) {
        if (!isActive) {
          return;
        }

        setPreviewState({
          status: "error",
          rendered: null,
          errorMessage:
            error instanceof Error
              ? error.message
              : "An unknown markdown rendering error occurred.",
        });
      }
    }, previewDebounceMs);

    return () => {
      isActive = false;
      window.clearTimeout(timeoutId);
    };
    // fontSize / codeFontSize / diagramFontSize / diagramTheme are applied in print CSS / Mermaid.
  }, [isEmbedded, markdown, pdfOptions, previewEnabled, previewRenderNonce]);

  return (
    <>
      <EditorFontStyles
        fontFamily={editorFontFamily}
        scriptFallbacks={scriptFallbacks}
      />
      <MobileTabs activeTab={activeTab} onTabChange={handleTabChange} />
      <WorkspaceToolbar
        canRedo={canRedo}
        canUndo={canUndo}
        onCommand={runEditorCommand}
        onRedo={redoFromToolbar}
        onUndo={undoFromToolbar}
        pdfOptions={pdfOptions}
        onPdfOptionsChange={updatePdfOptions}
        editorFontFamily={editorFontFamily}
        editorFontSize={editorFontSize}
        onEditorFontFamilyChange={setEditorFontFamily}
        onEditorFontSizeChange={setEditorFontSize}
        onClosePopovers={registerToolbarClose}
        filename={filename}
        onFilenameChange={updateFilename}
        canDownload={canDownloadPdf}
        downloadState={downloadState}
        onDownload={downloadPdf}
        showPdfSettings
        showOpenFile
        onOpenMarkdownFile={replaceDocumentFromFile}
        showClear
        canClear={markdown.trim().length > 0 || filename.trim().length > 0}
        onClearDocument={clearDocument}
        showOpenFullscreen={isEmbedded}
        editorSource={editorSource}
        fallbackMarkdown={seedMarkdownRef.current}
        fallbackFilename={initialFilename}
        chatShareProvider={chatShareProvider}
        chatShareImportUrl={chatShareImportUrl}
        repoMarkdownImport={repoMarkdownImport}
        repoMarkdownImportUrl={repoMarkdownImportUrl}
        lineNumberDigits={Math.max(String(markdown.split("\n").length).length, 2)}
      />
      <ResizableSplit ratio={splitRatio} onRatioChange={setSplitRatio}>
        <EditorPane
          ref={editorPaneRef}
          activeTab={activeTab}
          fontFamily={editorFontFamily}
          fontSize={editorFontSize}
          markdown={markdown}
          scriptFallbacks={scriptFallbacks}
          dir={documentDir}
          lang={documentLang}
          onMarkdownChange={updateMarkdown}
          onRedo={redoMarkdown}
          onUndo={undoMarkdown}
          scrollContainerRef={editorScrollRef}
        />
        <PreviewPane
          activeTab={activeTab}
          previewEnabled={previewEnabled}
          previewVisible={previewVisible}
          pdfOptions={pdfOptions}
          previewState={previewState}
          scriptFallbacks={scriptFallbacks}
          dir={documentDir}
          lang={documentLang}
          scrollContainerRef={previewScrollRef}
          onClosePopovers={closePreviewToolbarMenu}
          onPageCountChange={setPreviewPageCount}
          onUpdatingChange={setPreviewUpdating}
          markdown={markdown}
          sampleMarkdown={seedMarkdownRef.current}
          preferSampleSnapshot={isEmbedded}
        />
      </ResizableSplit>
      <StatusBar
        markdown={markdown}
        pageCount={previewPageCount}
        isUpdating={previewVisible && previewUpdating}
      />
    </>
  );
});
